import type { ReactElement } from 'react';
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { HeapCustomEvent, PaymentService, PaymentUpdateFunnel, Route } from 'config';
import Braintree from 'services/Braintree';
import HeapAnalytics from 'services/HeapAnalytics';
import { isEmpty } from 'utils/objects';

import useAddCreditCard from 'hooks/useAddCreditCard';
import useCurrentUser from 'hooks/useCurrentUser';
import useAsyncEffect from 'hooks/useAsyncEffect';
import useAddresses from 'hooks/useAddresses';

import Modal from 'components/shared/Modal';
import PaymentView, { PaymentProvider } from 'components/shared/PaymentView';
import BillingAddressView from 'components/shared/BillingAddressView';

type Props = {
  show: boolean;
  onClose: () => void;
};

function PaymentModal({ show, onClose }: Props): ReactElement {
  const [ccNumber, setCCNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvc, setCvc] = useState('');
  const [error, setError] = useState('');

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isRequestInProgress, setRequestInProgress] = useState(false);
  const [isBillingAddressActive, setIsBillingAddressActive] = useState(false);

  const addCreditCard = useAddCreditCard();
  const { currentUser, reload: reloadUser } = useCurrentUser();
  const { addresses } = useAddresses();
  const navigate = useNavigate();

  const billingAddressViewRef = useRef(null);

  const { billing_address: billingAddress, payment_method: paymentMethod } = currentUser;
  const hasCreditCardMethod = paymentMethod && paymentMethod.cc_brand;
  const hasBillingAddress = !isEmpty(billingAddress);
  const showBillingAddressCol = (hasCreditCardMethod
    || hasBillingAddress
    || selectedProvider === PaymentProvider.CREDIT_CARD);

  useAsyncEffect(async () => Braintree.initialize(), []);

  const handleClose = () => {
    setIsBillingAddressActive(false);
    setRequestInProgress(false);
    onClose();
  };

  const onSuccess = async () => {
    const wasSuspended = currentUser.isSuspended;

    await reloadUser();
    setSelectedProvider(null);
    handleClose();

    if (!currentUser.isSuspended && wasSuspended) {
      navigate(Route.UPCOMING_MENUS);
    }
  };

  const triggerSubmitBillingAddress = () => {
    if (billingAddressViewRef.current) {
      // @ts-expect-error TODO: fix null safety
      billingAddressViewRef.current?.handleSubmit();
    }
  };

  return (
    <Modal
      show={show}
      onClose={handleClose}
      className={`c-profile__forms-payment-modal${showBillingAddressCol ? '-lg' : ''}`}
      header={(
        <Modal.Header className="c-heading--brand-6" center={false}>
          <div className="row">
            <div className={`col-${showBillingAddressCol ? '6' : '12'}`}>
              Edit Payment
            </div>
            {showBillingAddressCol && (
              <div className="col-6 c-profile__top-billing-address-title">
                Billing Address
              </div>
            )}
          </div>
        </Modal.Header>
      )}
      body={(
        <div className="row">
          <div className={`col-md-${showBillingAddressCol ? '6' : '12'}`}>
            <PaymentView
              ccNumber={ccNumber}
              expMonth={expMonth}
              expYear={expYear}
              cvc={cvc}
              error={error}
                // @ts-expect-error TODO: fix null safety
              selectedProvider={selectedProvider}
              setCCNumber={setCCNumber}
              setExpMonth={setExpMonth}
              setExpYear={setExpYear}
              setCvc={setCvc}
                // @ts-expect-error TODO: fix null safety
              setSelectedProvider={setSelectedProvider}
              onSuccess={onSuccess}
            />
          </div>
          {showBillingAddressCol && (
          <div className="col-md-6">
            <div className="c-profile__billing-address-title c-heading--brand-6">
              Billing Address
            </div>
            {!hasBillingAddress || isBillingAddressActive ? (
              <BillingAddressView
                ref={billingAddressViewRef}
                address={isEmpty(billingAddress) ? addresses[0] : billingAddress}
                userID={currentUser?.id}
                submitButtonLabel="Save"
                hideSubmitButton={!hasBillingAddress}
                compactDesign
                onSuccess={() => {
                  setIsBillingAddressActive(false);
                  HeapAnalytics.track(HeapCustomEvent.UPDATE_BILLING_ADDRESS);
                  reloadUser();
                }}
              />
            ) : (
              <>
                {`${billingAddress.firstLine} ${billingAddress.secondLine || ''}`}
                <br />
                {billingAddress.lastLine}
                <br />
                {hasCreditCardMethod && (
                <button
                  type="button"
                  className="c-button c-button--primary c-profile__billing-address-edit"
                  disabled={isRequestInProgress}
                  onClick={() => setIsBillingAddressActive(true)}
                >
                  Edit
                </button>
                )}
              </>
            )}
          </div>
          )}
        </div>
      )}
      footer={(
        <>
          <button
            type="button"
            className="c-button c-button--primary-link"
            onClick={handleClose}
          >
            Cancel
          </button>

          <button
            type="button"
            className="c-button c-button--primary"
            disabled={!(
              selectedProvider === PaymentProvider.CREDIT_CARD
                && ccNumber && expMonth && expYear && cvc
                && !isRequestInProgress
            )}
            onClick={() => {
              addCreditCard(
                ccNumber,
                cvc,
                expMonth,
                expYear,
                setError,
                setRequestInProgress,
                () => {
                  HeapAnalytics.trackSuccessfullyAuthPayment(
                    PaymentUpdateFunnel.USER_PROFILE,
                    PaymentService.CREDIT_CARD,
                  );

                  onSuccess();
                },
                billingAddress,
              );
              triggerSubmitBillingAddress();
            }}
          >
            Update
          </button>
        </>
      )}
    />
  );
}

export default PaymentModal;
